import * as Yup from "yup";
import { isArray, isFile } from "utils/validation";
import { rfxSubmission } from "newui/features/Rfx/Submission";
import { RFX_QUESTION_TYPE } from "newui/features/Rfx/Submission/constants";
import { TextLength } from "newui/features/Rfx/types/component-types";
import { betterClearHTMLTags } from "components/FormInputs/FormattedInput";

export const allValidationSchema = (
	returnable,
	quote,
	isGuest,
	hasDocuments,
	hasPasProjects,
	hasResources
) => {
	const quoteValidation = {
		quote_files: Yup.mixed().when("quote_type", {
			is: (value) => value === "file",
			then: Yup.mixed()
				.required()
				.test("len", "This field is required", (val) => val && val.length > 0),
		}),
		quote_answer: Yup.mixed().when("quote_type", {
			is: (value) => value === "form",
			then: Yup.object().shape(quotesSchema(quote)),
		}),
		flexible_quote_files: Yup.mixed()
			.transform((value) => {
				if (Array.isArray(value) && value.length > 0) {
					return value[0].path;
				}
				return value;
			})
			.nullable(),
	};

	const returnableScheduleValidation = {
		returnable_schedule_file_responses: Yup.mixed().when("returnables_type", {
			is: (val) => val === "file",
			then: Yup.mixed()
				.required()
				.test("len", "This field is required", (val) => val && val.length > 0),
		}),

		question_answer: Yup.object()
			.shape(
				returnableSchema(
					returnable,
					isGuest,
					hasDocuments,
					hasPasProjects,
					hasResources
				)
			)
			.when("returnables_type", {
				is: "form",
				then: (schema) => schema.required("Required"),
				otherwise: (schema) => schema.notRequired(),
			}),
	};

	const validationSchema = Yup.object().shape({
		...quoteValidation,
		...returnableScheduleValidation,
	});
	return validationSchema;
};

const validateTextAndTextArea = (required, maxLimit) => {
	const testTextLength = (val) => {
		const textLength =
			!val || val.length === 0 ? 0 : betterClearHTMLTags(val).length;

		return val ? textLength <= maxLimit : true;
	};

	if (required) {
		return Yup.string()
			.required("Required")
			.nullable()
			.test("len", "Input has exceeded the max character", (val) =>
				testTextLength(val)
			);
	}

	return Yup.string()
		.nullable()
		.test("len", "Input has exceeded the max character", (val) =>
			testTextLength(val)
		);
};

const getSmartFieldSchema = (hasRequiredItem, question, isRequired) => {
	if (hasRequiredItem) {
		return isRequired
			? Yup.mixed().test(
					"either-main-or-additional",
					"Please select at least one",
					function (value) {
						const additionalValue =
							this.parent?.[`question-${question.id}.additional`];
						return (
							(Array.isArray(value) && value.length > 0) ||
							(Array.isArray(additionalValue) && additionalValue.length > 0)
						);
					}
				)
			: Yup.array().of(Yup.number());
	}

	return validateTextAndTextArea(
		isRequired,
		rfxSubmission.helpers.getTextLengthValue(TextLength.L4500)
	);
};

export const returnableSchema = (
	returnableList,
	isGuest,
	hasDocuments,
	hasPasProjects,
	hasResources
) => {
	let returnableSchema = {};

	const schemaCheck = (
		type,
		required,
		hasDocuments,
		hasPasProjects,
		hasResources
	) => {
		if (isArray(type)) {
			return required
				? Yup.array()
						.of(Yup.string("Required").required("Required"))
						.min(1, "Required")
				: Yup.array().of(Yup.string());
		} else if (rfxSubmission.helpers.isSmartField(type)) {
			if (isGuest) {
				if (type === "corporate_documents") {
					return required
						? Yup.array()
								.of(Yup.mixed("Required").required("Required"))
								.test("empty", "Required", (value) => value?.length > 0)
						: Yup.array().of(Yup.mixed());
				} else {
					return required ? Yup.mixed().required("Required") : Yup.mixed();
				}
			} else {
				/**
				 * if supplier but no documents show upload field
				 */
				if (type === "corporate_documents" && !hasDocuments) {
					return required
						? Yup.mixed().required("Required")
						: Yup.array().of(Yup.mixed());
				} else if (
					(type === "projectexp" || type === "project_experience") &&
					!hasPasProjects
				) {
					return validateTextAndTextArea(
						required,
						rfxSubmission.helpers.getTextLengthValue(TextLength.L4500)
					);
				} else if (
					(type === "keypersonel" || type === "key_personnel") &&
					!hasResources
				) {
					return validateTextAndTextArea(
						required,
						rfxSubmission.helpers.getTextLengthValue(TextLength.L4500)
					);
				} else {
					return required
						? Yup.array()
								.nullable()
								.test(
									"is-non-empty-array",
									"Please select at least one",
									(val) => {
										if (val === null) {
											return false;
										}
										return Array.isArray(val) && val.length > 0;
									}
								)
						: Yup.array().of(Yup.number());
				}
			}
		} else if (isFile(type)) {
			return required
				? Yup.array()
						.of(Yup.mixed("Required").required("Required"))
						.test("empty", "Required", (value) => value?.length > 0)
				: Yup.array().of(Yup.mixed());
		} else if (type === "table") {
			return Yup.mixed().nullable();
		} else if (type === "text") {
			return validateTextAndTextArea(
				required,
				rfxSubmission.helpers.getTextLengthValue(TextLength.SHORT)
			);
		} else if (type === "textarea") {
			return validateTextAndTextArea(
				required,
				rfxSubmission.helpers.getTextLengthValue(TextLength.LONG)
			);
		} else {
			return required ? Yup.mixed().required("Required") : Yup.mixed();
		}
	};

	returnableList?.forEach((section) => {
		section.questionnaire?.forEach((question) => {
			const questionType = question?.fields?.type;
			let schemaToUse = null;
			const isRequired = question?.required;

			if (question.hasComment) {
				returnableSchema[`question-${question.id}-comment`] =
					validateTextAndTextArea(
						false,
						rfxSubmission.helpers.getTextLengthValue(TextLength.SHORT)
					);
			}

			switch (questionType) {
				case RFX_QUESTION_TYPE.TABLE: {
					schemaToUse = isRequired
						? Yup.array().of(
								Yup.string()
									.nullable()
									.test(
										"empty",
										"This is a required field",
										(value) => value?.length > 0
									)
									.required("This is a required field")
							)
						: Yup.array().of(Yup.string().nullable());
					break;
				}

				case RFX_QUESTION_TYPE.TABLE_ROWS: {
					if (isRequired) {
						schemaToUse = Yup.array()
							.of(
								Yup.array().of(
									Yup.object({
										id: Yup.string().nullable(),
										value: Yup.string()
											.transform((val) =>
												val === null || val === undefined ? "" : val
											)
											.required("Value is required")
											.test("not-empty", "Value is required", (val) => {
												return val?.trim() !== "";
											}),
									})
								)
							)
							.min(1, "At least one row is required");
					} else {
						schemaToUse = Yup.array().of(
							Yup.array().of(
								Yup.object({
									id: Yup.string().nullable(),
									value: Yup.string().transform((val) =>
										val === null || val === undefined ? "" : val
									),
								})
							)
						);
					}

					break;
				}
				case RFX_QUESTION_TYPE.CORPORATE_DOCUMENTS: {
					if (hasDocuments) {
						schemaToUse = getSmartFieldSchema(true, question, isRequired);
						break;
					}

					schemaToUse = isRequired
						? Yup.mixed().required("Required")
						: Yup.array().of(Yup.mixed());
					break;
				}
				case RFX_QUESTION_TYPE.PROJECT_EXPERIENCE:
					schemaToUse = getSmartFieldSchema(
						hasPasProjects,
						question,
						isRequired
					);
					break;
				case RFX_QUESTION_TYPE.KEY_PERSONNEL:
					schemaToUse = getSmartFieldSchema(hasResources, question, isRequired);
					break;
				default: {
					schemaToUse = schemaCheck(
						questionType,
						question.required,
						hasDocuments,
						hasPasProjects,
						hasResources
					);
				}
			}

			returnableSchema[`question-${question.id}`] = schemaToUse;
		});
	});

	return returnableSchema;
};

export const quotesSchema = (quoteList) => {
	let quotesSchema = {};

	quoteList?.forEach((quote) => {
		return quote.items.forEach((item) => {
			return (quotesSchema[`quote-${item.id}`] = Yup.object().shape({
				quantity: Yup.number("Required")
					.required("Required")
					.test("is-positive", "Required", (value) => parseFloat(value) >= 0),
				rate: Yup.number("Required")
					.required("Required")
					.test("is-positive", "Required", (value) => parseFloat(value) >= 0),
				unit: Yup.number("Required")
					.required("Required")
					.test("is-positive", "Required", (value) => parseInt(value) > 0),
			}));
		});
	});
	return quotesSchema;
};
